import React, { useEffect, useState, useRef } from "react";
import { IconX } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./VideoPlayerModal.module.css";

export default function VideoPlayerModal({ isOpen, videoUrl, onClose }) {
  const [key, setKey] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    if (isOpen) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Reset video when modal opens
  useEffect(() => {
    if (isOpen) {
      setKey((prev) => prev + 1);
    }
  }, [isOpen, videoUrl]);

  // Ensure video plays on load
  useEffect(() => {
    if (isOpen && videoRef.current) {
      const playVideo = async () => {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.log("Autoplay prevented:", error);
        }
      };

      // Try to play when video can play
      videoRef.current.addEventListener("loadeddata", playVideo);

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener("loadeddata", playVideo);
        }
      };
    }
  }, [isOpen, key]);

  if (!isOpen || !videoUrl) return null;

  // Properly encode the URL
  const properUrl = videoUrl.replace(/\+/g, "%20");

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="modal"
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose?.();
            }
          }}
        >
          <div className={styles.modal}>
            <button type="button" className={styles.controlBtn} onClick={onClose} aria-label="Close">
              <IconX size={24} />
            </button>
            <div className={styles.playerWrapper}>
              <video
                ref={videoRef}
                key={key}
                className={styles.video}
                controls
                autoPlay
                loop
                muted={false}
                playsInline
                preload="auto"
                controlsList="nodownload"
              >
                <source src={properUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
